/* eslint-disable consistent-return */
/* eslint-disable complexity */
import React, { useCallback, useMemo, useEffect, useContext } from 'react';
import { useGetGlobalAttributes, useGetXstyledProps, useOwnerProps } from '@elliemae/ds-props-helpers';
import { useFloatingContext, FloatingWrapper, type DSHookFloatingContextT } from '@elliemae/ds-floating-context';
import ComboBoxContext from '../../ComboBoxCTX.js';
import { StyledContainer, StyledPopperWrapper } from './styled.js';
import { Controls } from '../controls/index.js';
import { MenuList } from '../menu-list/index.js';
import { ComboboxDataTestid } from '../../ComboboxDataTestids.js';
import { LiveRegion } from '../LiveRegion.js';
import { useOnClickOutside } from '../../config/useClickOutside.js';

export const Container = (): JSX.Element => {
  const { props, internalRef, setMenuState, menuState } = useContext(ComboBoxContext);

  const { inline, startPlacementPreference, placementOrderPreference, isLoading, isSkeleton, applyAriaDisabled } =
    props;
  const globalAttributes = useGetGlobalAttributes(props) as ReturnType<typeof useGetGlobalAttributes> & {
    'data-testid'?: string;
  };
  const { zIndex, ...xStyledProps } = useGetXstyledProps(props);
  const { getOwnerProps, getOwnerPropsArguments } = useOwnerProps(props);

  const correctZIndex = zIndex ? parseInt(zIndex as string, 10) : undefined;
  // Removing possible collisionable props
  const { className, 'data-testid': dataTestId } = globalAttributes;
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuState(false, 'blur');
    if (internalRef?.current) internalRef.current.blur();
  }, [internalRef, setMenuState]);

  useEffect(() => {
    const closeMenuOnWindowBlur = () => {
      if (menuState) setMenuState(false, 'blur');
    };

    window.addEventListener('blur', closeMenuOnWindowBlur);

    return () => {
      window.removeEventListener('blur', closeMenuOnWindowBlur);
    };
  }, [menuState, setMenuState]);

  const config = useMemo(
    () => ({
      placement: 'bottom' as DSHookFloatingContextT.PopperPlacementsT,
      withoutAnimation: true,
      startPlacementPreference,
      placementOrderPreference,
      zIndex: correctZIndex,
      customOffset: [0, 5] as [number, number],
      handleCloseMenu,
      externallyControlledIsOpen: menuState,
    }),
    [startPlacementPreference, placementOrderPreference, correctZIndex, handleCloseMenu, menuState],
  );

  const { refs, floatingStyles, context } = useFloatingContext(config);

  const hideTooltip = useCallback(() => {
    setMenuState(false, 'blur');
  }, [setMenuState]);

  useOnClickOutside(refs.reference, hideTooltip, refs.floating);

  return (
    <StyledContainer
      data-testid={dataTestId ?? ComboboxDataTestid.CONTAINER}
      innerRef={refs.setReference}
      className={className}
      applyAriaDisabled={applyAriaDisabled}
      {...xStyledProps}
      getOwnerProps={getOwnerProps}
      getOwnerPropsArguments={getOwnerPropsArguments}
    >
      <LiveRegion />
      <Controls />

      {menuState && inline ? <MenuList /> : null}
      {menuState && !inline && refs.reference ? (
        <FloatingWrapper
          innerRef={refs.setFloating}
          floatingStyles={floatingStyles}
          isOpen={menuState}
          context={context}
          {...props.popperProps}
          key={`${isLoading}${isSkeleton}`} // to force remount when loading/skeleton changes
        >
          <StyledPopperWrapper tabIndex={-1} onMouseDown={handleMouseDown} applyAriaDisabled={applyAriaDisabled}>
            <MenuList />
          </StyledPopperWrapper>
        </FloatingWrapper>
      ) : null}
    </StyledContainer>
  );
};
